<script setup lang="ts">
import {
  Table,
  TableHeader,
  TableHead,
  TableRow,
  TableBody,
  TableCell
} from '~/components/ui/table'
import { CurrencyIcon } from '~/components/ui/currency-icon'
import type { IResponseData } from '~/types'

type TExchangeRateMap = Record<string, [number, string, number]>
interface ICurrencyRate {
  code: string
  id: number
  rate: number
  change: number
  title: string
  symbol: string
  quantity: number
}

const currenciesStore = useCurrencyStore()
const { list } = storeToRefs(currenciesStore)

const { data } = await useFetch<IResponseData<TExchangeRateMap>>('/api/rates/nbkr-cached', {
  server: true,
  lazy: false
})

const rates = computed<ICurrencyRate[]>(() => {
  if (!data.value || !list.value.length) {
    return []
  }

  return Object.entries(data.value).map(([code, [id, rateStr, change]]) => {
    const currencyInfo = list.value.find((currency) => currency.id === id)

    return {
      code,
      id,
      rate: parseFloat(rateStr),
      change,
      title: currencyInfo?.title || code,
      symbol: currencyInfo?.symbol || code.toUpperCase(),
      quantity: currencyInfo?.quantity || 1
    }
  })
})

if (rates.value.length > 0) {
  const { t } = useI18n()
  const serviceItems = rates.value.map((rate) => ({
    '@type': 'Service',
    name: `${t('widget.rates.nbkr.official_rate_for')} ${rate.title} (${rate.code.toUpperCase()})`,
    description: `${t('widget.rates.nbkr.official_exchange_rate')} ${rate.title} ${t('widget.rates.nbkr.from_national_bank')}.`,
    provider: {
      '@type': 'Organization',
      name: t('widget.rates.nbkr.national_bank'),
      url: 'https://www.nbkr.kg/'
    },
    serviceOutput: {
      '@type': 'MonetaryAmount',
      currency: 'KGS',
      value: rate.rate,
      minValue: rate.rate,
      maxValue: rate.rate
    }
  }))

  useSchemaOrg([
    {
      '@type': 'WebPage',
      name: t('widget.rates.nbkr.official_currency_rates_title'),
      description: t('widget.rates.nbkr.daily_currency_rates_desc'),
      offers: serviceItems,
      dateModified: new Date().toISOString()
    }
  ])
}
</script>

<template>
  <div v-if="!data || !list.length" class="flex items-center justify-center p-8">
    <div class="text-gray-500">{{ $t('widget.rates.nbkr.loading') }}</div>
  </div>
  <Table v-else>
    <TableHeader>
      <TableRow>
        <TableHead>{{ $t('common.currency') }}</TableHead>
        <TableHead class="text-center">{{ $t('common.code') }}</TableHead>
        <TableHead class="text-center">{{ $t('common.symbol') }}</TableHead>
        <TableHead class="text-center">{{ $t('widget.rates.nbkr.units') }}</TableHead>
        <TableHead class="text-center">{{ $t('common.rate') }}</TableHead>
        <TableHead class="text-center">{{ $t('widget.rates.nbkr.changes') }}</TableHead>
      </TableRow>
    </TableHeader>
    <TableBody>
      <TableRow v-for="rate in rates" :key="rate.id">
        <TableCell class="flex items-center gap-3">
          <CurrencyIcon :code="rate.code" :title="rate.title" size="lg" />
          <NuxtLinkLocale
            :to="`/rates/nbkr/${rate.code}`"
            :aria-label="`${$t('common.more_about')} ${rate.title}`"
            class="text-blue-600 hover:underline"
          >
            {{ rate.title }}
          </NuxtLinkLocale>
        </TableCell>
        <TableCell class="bg-gray-100 text-center uppercase">{{ rate.code }}</TableCell>
        <TableCell class="text-center">{{ rate.symbol }}</TableCell>
        <TableCell class="bg-gray-100 text-center">{{ rate.quantity }}</TableCell>
        <TableCell class="text-center">{{ rate.rate }}</TableCell>
        <TableCell
          class="bg-gray-100 text-center"
          :class="rate.change > 0 ? 'text-green-500' : 'text-red-500'"
        >
          <span v-if="rate.change !== 0">{{ rate.change }}</span>
        </TableCell>
      </TableRow>
    </TableBody>
  </Table>
</template>
